import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Package, CheckCircle2, Clock, Copy, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function OrgDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [org, setOrg] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  const load = useCallback(async () => {
    try {
      const [statsRes, orgRes, lbRes] = await Promise.all([
        api.get('/orgs/me/stats'),
        api.get('/orgs/me'),
        api.get('/orgs/me/leaderboard', { params: { limit: 5 } }),
      ]);
      setStats(statsRes.data);
      setOrg(orgRes.data.organization);
      setLeaderboard(lbRes.data.leaderboard || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load dashboard');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const copyCode = () => {
    if (!org?.code) return;
    navigator.clipboard.writeText(org.code);
    toast.success(`Org code ${org.code} copied`);
  };

  return (
    <div className="space-y-4" data-testid="org-dashboard">
      {org && (
        <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 flex items-center gap-3" data-testid="org-header-card">
          <div className="w-12 h-12 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
            {org.logo ? (
              <img src={org.logo} alt="" className="w-full h-full rounded-xl object-cover" />
            ) : (
              <Building2 className="w-6 h-6 text-[#0B4F3F]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#0B1620] font-['Bricolage_Grotesque'] truncate">{org.name}</p>
            <p className="text-[11px] text-[#5B6675] uppercase tracking-wide">{org.org_type}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                org.status === 'approved' ? 'bg-[#15803D]/10 text-[#15803D]' :
                org.status === 'pending' ? 'bg-[#0B4F3F]/10 text-[#0B4F3F]' :
                'bg-[#B91C1C]/10 text-[#B91C1C]'
              }`}>{org.status.toUpperCase()}</span>
            </div>
          </div>
          <button
            data-testid="org-copy-code-btn"
            onClick={copyCode}
            className="text-[10px] font-mono font-bold bg-[#EFF2F7] text-[#0B4F3F] px-2.5 py-1.5 rounded-lg flex items-center gap-1"
            title="Org code"
          >
            {org.code} <Copy className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3" data-testid="org-stats">
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <Users className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
          <p className="text-2xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{stats?.total_farmers ?? '—'}</p>
          <p className="text-[10px] text-[#5B6675]">Farmers</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <Package className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
          <p className="text-2xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{stats?.total_produce ?? '—'}</p>
          <p className="text-[10px] text-[#5B6675]">Total posts</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <CheckCircle2 className="w-5 h-5 mx-auto text-[#15803D] mb-1" />
          <p className="text-2xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{stats?.active_produce ?? '—'}</p>
          <p className="text-[10px] text-[#5B6675]">Available</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 text-center">
          <Clock className="w-5 h-5 mx-auto text-[#0B4F3F] mb-1" />
          <p className="text-2xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{stats?.open_requests ?? '—'}</p>
          <p className="text-[10px] text-[#5B6675]">Open requests</p>
        </div>
      </div>

      {/* Top performers */}
      <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4">
        <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque'] mb-3">Top performing farmers</h3>
        {leaderboard.length === 0 ? (
          <p className="text-xs text-[#5B6675]/70 text-center py-4">No data yet</p>
        ) : (
          leaderboard.map((f, i) => (
            <div key={f.farmer_id} className="flex items-center gap-3 py-2 border-b border-[#E4E8EF] last:border-0">
              <span className="text-xs font-bold text-[#5B6675] w-5">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0B1620] truncate">{f.name}</p>
                <p className="text-[11px] text-[#5B6675]">{f.post_count} posts · {f.avg_rating.toFixed(1)}★ ({f.review_count})</p>
              </div>
              <span className="text-xs font-bold text-[#0B4F3F]">{f.score}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
